import { Component, Inject, Input, OnInit } from '@angular/core';
import { controlHttp, FormFieldConfig, VarType } from '../../shared/models/form.models';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { ProductosService } from '../../services/productos.service';
import { ToastrService } from 'ngx-toastr';
import { GenericFormHandlerService } from '../../shared/generic-crud.service';
import { UnidadMedida } from '../../enums/unidad-medida.enum';
import { TipoProducto, TIPO_PRODUCTO_LABELS } from '../../enums/tipo-producto.enum';
import { ProveedorService } from '../../services/proveedor.service';
import { SuppliersDialogComponent } from 'src/app/suppliers/suppliers-dialog/suppliers-dialog.component';

@Component({
  selector: 'app-product-dialog',
  templateUrl: './product-dialog.component.html',
  styleUrls: ['./product-dialog.component.scss']
})
export class ProductoDialogComponent extends GenericFormHandlerService implements OnInit {
  isVendedor = false;
  isReadOnly = false;

  unidadesMedida = Object.values(UnidadMedida).map(value => ({
    value,
    label: this.getUnidadMedidaLabel(value)
  }));

  tiposProducto = Object.values(TipoProducto).map(value => ({
    value,
    label: TIPO_PRODUCTO_LABELS[value]
  }));

  constructor(
    protected productosService: ProductosService,
    protected proveedorService: ProveedorService,
    protected toastr: ToastrService,
    @Inject(MAT_DIALOG_DATA) public data: DialogData,
    public dialogRef?: MatDialogRef<ProductoDialogComponent>
  ) {
    super(productosService, toastr, dialogRef);
    this.configureBasedOnData();
  }

  ngOnInit(): void {
    // Verificar si es vendedor
    this.isVendedor = this.data.isVendedor || false;
    this.isReadOnly = this.isVendedor; // Si es vendedor, solo lectura
    
    this.cargarFrm();
    
    // Si es vendedor, establecer como modo lectura
    if (this.isVendedor) {
      this.currentOperation = controlHttp.READ;
    }
    
    this.initializeForm(this.data.isEdit && !this.isVendedor).then(() => {
      console.log('Form ready, controls:', this.mainForm.controls);
      
      // Si es vendedor, deshabilitar todos los campos
      if (this.isVendedor) {
        this.mainForm.disable();
      }
    });
  }

  private configureBasedOnData(): void {
    // Si es vendedor, siempre es modo lectura
    if (this.data.isVendedor) {
      this.currentOperation = controlHttp.READ;
    } else {
      this.currentOperation = this.data.isEdit ? controlHttp.UPDATE : controlHttp.CREATE;
    }

    if (this.data.isEdit && this.data.producto) {
      this.apiIdentifier = this.data.producto.id;
    }
  }
  private cargarFrm() {

    this.formSections = {
      basic: [
      // Campos básicos: nombre, código, descripción, etc.
      { //NOMBRE
        fieldName: 'nombre',
        dataType: VarType.TEXT,
        isVisible: true,
        initialValue: this.data?.producto?.nombre || '',
        fieldLabel: 'Nombre del Producto',
        width: 50,
        validations: [
          { type: 'required', message: 'El nombre es requerido' },
          { type: 'maxLength', value: 100, message: 'Máximo 100 caracteres' }
        ]
      },
      {//PROVEEDOR
        fieldName: 'proveedorId',
        dataType: VarType.SELECTDINAMIC,
        isVisible: true,
        initialValue: this.data?.producto?.proveedorId || null,
        fieldLabel: 'Proveedor',
        width: 50,
        service: this.proveedorService, // Inyectar el servicio de proveedores
        //Agregar componente Dialog en caso de querer agregar uno mismo ahi ejemplo SuppliersDialogComponent
        openDialogComponent: SuppliersDialogComponent,
        //filter: { activo: true }, // Filtro personalizado
        labelField: 'nombre',
        valueField: 'id',
      },
      {//CODIGO
        fieldName: 'codigo',
        dataType: VarType.TEXT,
        isVisible: false,
        initialValue: this.data?.producto?.codigo || '',
        fieldLabel: 'Código (SKU)',
        width: 50,
      },
      {//DESCRIPCION
        fieldName: 'descripcion',
        dataType: VarType.TEXTAREA,
        isVisible: true,
        initialValue: this.data?.producto?.descripcion || '',
        fieldLabel: 'Descripción',
        width: 100,
      },
      {//PRECIO
        fieldName: 'precio',
        dataType: VarType.CURRENCY,
        isVisible: true,
        initialValue: this.data?.producto?.precio || 0,
        fieldLabel: 'Precio al público',
        width: 33,
        validations: [
          { type: 'required', message: 'El precio es requerido' },
          { type: 'min', value: 0.01, message: 'El precio debe ser mayor a 0' }
        ]
      },
      {//STOCK
        fieldName: 'stock',
        dataType: VarType.NUMBER,
        isVisible: true,
        initialValue: this.data?.producto?.stock || 0,
        fieldLabel: 'Stock Inicial',
        width: 33,
        validations: [
          { type: 'required', message: 'El stock es requerido' },
          { type: 'min', value: 0, message: 'El stock no puede ser negativo' }
        ]
      },
      {//UNIDADMEDIDA
        fieldName: 'unidadMedida',
        dataType: VarType.SELECT,
        isVisible: true,
        initialValue: this.data?.producto?.unidadMedida || UnidadMedida.GRAMOS,
        fieldLabel: 'Unidad de medida',
        width: 33,
        options: this.unidadesMedida,
      },
      {//TIPOPRODUCTO
        fieldName: 'tipoProducto',
        dataType: VarType.SELECT,
        isVisible: true,
        initialValue: this.data?.producto?.tipoProducto || TipoProducto.ESTANDAR,
        fieldLabel: 'Tipo de Producto',
        width: 33,
        options: this.tiposProducto,
      },
      {//PESO
        fieldName: 'peso',
        dataType: VarType.NUMBER,
        isVisible: true,
        initialValue: this.data?.producto?.peso || null,
        fieldLabel: 'Peso (g)',
        width: 33,
      },
      {//ETIQUETAS
        fieldName: 'etiquetas',
        dataType: VarType.CHIPS,
        isVisible: true,
        initialValue: this.data?.producto?.etiquetas || [],
        fieldLabel: 'Etiquetas (separadas por comas)',
        width: 100
      }

    ],
    details: [
      // Campos de detalles: ingredientes, beneficios, modo de uso, etc.
      {//INGREDIENTES
        fieldName: 'ingredientes',
        dataType: VarType.CHIPS,
        isVisible: true,
        initialValue: this.data?.producto?.ingredientes || '',
        fieldLabel: 'Ingredientes (separadas por comas)',
        width: 100
      },
      {//BENEFICIOS
        fieldName: 'beneficios',
        dataType: VarType.TEXTAREA,
        isVisible: true,
        initialValue: this.data?.producto?.beneficios || '',
        fieldLabel: 'Beneficios',
        width: 100
      },
      {//MODO USO
        fieldName: 'modoUso',
        dataType: VarType.TEXTAREA,
        isVisible: true,
        initialValue: this.data?.producto?.modoUso || '',
        fieldLabel: 'Modo de uso',
        width: 100
      }
    ],
    inventory: [
      // Campos de inventario: precio, stock, unidad de medida, etc.
      {//PRECIO MAYORISTA
        fieldName: 'precioMayorista',
        dataType: VarType.CURRENCY,
        isVisible: true,
        initialValue: this.data?.producto?.precioMayorista || null,
        fieldLabel: 'Precio mayorista',
        width: 33,
      },
      {
        fieldName: 'cantidadMinimaMayorista',
        dataType: VarType.NUMBER,
        isVisible: true,
        initialValue: this.data?.producto?.cantidadMinimaMayorista || null,
        fieldLabel: 'Mínimo para mayorista',
        width: 33,
        validations: [
          { type: 'min', value: 1, message: 'La cantidad mínima debe ser al menos 1' }
        ]
      },
    ],
    extraFields: [
      {//CONTENIDO
        fieldName: 'contenido',
        dataType: VarType.NUMBER,
        isVisible: true,
        initialValue: this.data?.producto?.contenido || 100,
        fieldLabel: 'Contenido por unidad',
        width: 33,
      },
      {//PAIS ORIGEN
        fieldName: 'paisOrigen',
        dataType: VarType.TEXT,
        isVisible: true,
        initialValue: this.data?.producto?.paisOrigen || '',
        fieldLabel: 'País de origen',
        width: 50
      },

      {//CONDICIONES ALMACENAMIENTO
        fieldName: 'condicionesAlmacenamiento',
        dataType: VarType.TEXT,
        isVisible: true,
        initialValue: this.data?.producto?.condicionesAlmacenamiento || '',
        fieldLabel: 'Condiciones de almacenamiento',
        width: 100
      },

      {//ES ORGANICO
        fieldName: 'esOrganico',
        dataType: VarType.CHECKBOX,
        isVisible: true,
        initialValue: this.data?.producto?.esOrganico || false,
        fieldLabel: 'Producto orgánico',
        width: 33
      },
      {//ES VEGANO
        fieldName: 'esVegano',
        dataType: VarType.CHECKBOX,
        isVisible: true,
        initialValue: this.data?.producto?.esVegano || false,
        fieldLabel: 'Producto vegano',
        width: 33
      },
      { //TIENE GLUTEN
        fieldName: 'tieneGluten',
        dataType: VarType.CHECKBOX,
        isVisible: true,
        initialValue: this.data?.producto?.tieneGluten || false,
        fieldLabel: 'Contiene gluten',
        width: 33
      },
      { //IMAGEN
        fieldName: 'imagenUrl',
        dataType: VarType.TEXT,
        isVisible: true,
        initialValue: this.data?.producto?.imagenUrl || '',
        fieldLabel: 'URL de imagen',
        width: 100
      },
    ]
    };
  }

  handleFormSubmit(event: Event): void {
    event.preventDefault();
    if (this.isVendedor) {
      // Si es vendedor, no permitir enviar el formulario
      return;
    }
    this.submitForm(this.mainForm.value);
  }

  onCancel(): void {
    if (this.hasUnsavedChanges && !this.isVendedor) {
      // Mostrar confirmación solo si no es vendedor
    } else {
      this.dialogRef?.close();
    }
  }
  private getUnidadMedidaLabel(value: any): string {
    const labels: {[key: string]: string} = {
      [UnidadMedida.GRAMOS]: 'Gramos (g)',
      [UnidadMedida.KILOGRAMOS]: 'Kilogramos (kg)',
      [UnidadMedida.MILILITROS]: 'Mililitros (ml)',
      [UnidadMedida.LITROS]: 'Litros (l)',
      [UnidadMedida.UNIDADES]: 'Unidades',
      [UnidadMedida.PAQUETE]: 'Paquetes',
      [UnidadMedida.CAJA]: 'Cajas',
      [UnidadMedida.SOBRE]: 'Sobres',
      [UnidadMedida.BOLSA]: 'Bolsas',
      [UnidadMedida.LATA]: 'Latas'
    };
    return labels[value] || value;
  }
}


interface DialogData {
  producto?: any;
  isEdit: boolean;
  isVendedor?: boolean;
}
